import React, { useMemo } from 'react';
import { Button, Card, Placeholder } from 'react-bootstrap';
import { Minus, Satellite, TrendingDown, TrendingUp } from 'lucide-react';
import { buildSeasonHistory } from './satelliteModel';
import { renderSatText, t, toneColour } from './satelliteText';

const TREND_ICON = { up: TrendingUp, down: TrendingDown, flat: Minus, unknown: Satellite };
const TREND_TONE = { up: 'good', down: 'watch', flat: 'good', unknown: 'neutral' };

/** A bar's fill, as the farmer app colours it: the current season solid, past ones soft. */
const barStyle = (bar) => {
  if (bar.state === 'loading') return { background: '#F0F5EF' };
  if (bar.state !== 'ready') return { background: 'repeating-linear-gradient(135deg,#EFF3EE 0 4px,#E0E6DF 4px 8px)' };
  const weak = bar.tone === 'bad' || bar.tone === 'watch';
  if (bar.isNow) return { background: weak ? '#B7791F' : '#2D7D46' };
  return { background: weak ? '#E8C98F' : bar.tone === 'good' ? '#B6D2B2' : '#D8E5D6' };
};

/**
 * "Season by season": crop greenness for the same 90 days of each of the last
 * five years and now, one bar a year, and what they add up to.
 * (farmer-app SeasonBarsCard; the numbers are buildSeasonHistory's)
 */
export default function SeasonSection({ seasons = [], onRetry }) {
  const model = useMemo(() => buildSeasonHistory(seasons), [seasons]);

  if (seasons.length && seasons.every((s) => s.status === 'loading')) {
    return (
      <Card body aria-busy="true">
        <Placeholder as="div" animation="glow">
          <Placeholder xs={6} className="mb-3" />
          <Placeholder xs={12} style={{ height: 150 }} />
        </Placeholder>
      </Card>
    );
  }

  if (seasons.length && seasons.every((s) => s.status === 'error')) {
    return (
      <div className="alert alert-warning d-flex align-items-center gap-2" style={{ justifyContent: 'space-between' }}>
        <span>{t('seasonLoadFailed')}</span>
        <Button size="sm" variant="outline-dark" onClick={onRetry}>
          {t('retry')}
        </Button>
      </div>
    );
  }

  const Icon = TREND_ICON[model.trend];
  const colour = toneColour(TREND_TONE[model.trend]);
  return (
    <Card body>
      <div className="d-flex align-items-center gap-2 fw-bold" style={{ color: colour }}>
        <Icon size={18} />
        <span>{renderSatText(model.headline)}</span>
      </div>
      <p className="small text-muted mb-3">{renderSatText(model.sub)}</p>

      <div className="d-flex align-items-end gap-2" style={{ height: 170, maxWidth: 640 }} role="list">
        {model.bars.map((bar) => (
          // justifyContent is inline, not Bootstrap's class: the app-wide mobile rules in
          // styles/reports.css force every `.d-flex.justify-content-end` to full width.
          <div
            key={`${bar.year}-${bar.isNow}`}
            role="listitem"
            className="d-flex flex-column align-items-center gap-1 h-100"
            style={{ flex: '1 1 0', minWidth: 0, justifyContent: 'flex-end' }}
          >
            <span className={`small fw-bold text-nowrap ${bar.isNow ? '' : 'text-muted'}`}>{bar.state === 'ready' ? bar.value : '—'}</span>
            <div
              className="w-100"
              style={{ height: `${bar.heightPct}%`, borderRadius: '8px 8px 4px 4px', ...barStyle(bar) }}
              title={bar.state === 'missing' ? t('seasonMissing') : undefined}
            />
            <small className={`text-nowrap ${bar.isNow ? 'fw-bold' : 'text-muted'}`}>{bar.isNow ? t('seasonNow') : bar.year}</small>
          </div>
        ))}
      </div>
      <small className="d-block text-muted mt-3">{t('seasonScaleNote')}</small>
    </Card>
  );
}
